import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { cx } from "@/utils/cx";

export type PageHeaderTitleLevel = "h1" | "h2";

export type PageHeaderProps = Omit<
	ComponentPropsWithoutRef<"header">,
	"title" | "children"
> & {
	/** Título da página. Segue a tipografia de referência de `/infractions`. */
	title: ReactNode;
	/** Texto de apoio opcional, exibido abaixo do título. */
	subtitle?: ReactNode;
	/**
	 * Ações da página. Use `Button` de `@components/button/button-untitledui`
	 * com `size="sm"`: criação em `primary` e exportação em `secondary`.
	 */
	actions?: ReactNode;
	/** Nível do título. O padrão `h1` atende o título principal da página. */
	titleAs?: PageHeaderTitleLevel;
	/** Classes extras do título, para ajustes de layout. */
	titleClassName?: string;
	/** Classes extras do subtítulo, para ajustes de layout. */
	subtitleClassName?: string;
};

/**
 * Cabeçalho de página compartilhado.
 *
 * Composição baseada no cabeçalho de Gestão de Infrações: título `text-2xl`
 * semibold com `leading-8`, subtítulo `text-sm` secundário com `leading-5` e
 * espaço de 8 px entre eles. Não inclui ícone no título por padrão. As ações
 * usam o mesmo espaço de 16 px dos botões de Infrações e quebram para a linha
 * seguinte quando falta largura.
 */
export function PageHeader({
	title,
	subtitle,
	actions,
	titleAs: Title = "h1",
	className,
	titleClassName,
	subtitleClassName,
	...props
}: PageHeaderProps) {
	return (
		<header
			className={cx(
				"flex flex-wrap items-start justify-between gap-4",
				className
			)}
			data-slot="page-header"
			{...props}
		>
			<div className="flex min-w-0 max-w-3xl flex-col gap-2">
				<Title
					className={cx(
						"font-semibold text-2xl text-primary leading-8",
						titleClassName
					)}
				>
					{title}
				</Title>
				{subtitle ? (
					<p
						className={cx(
							"text-secondary text-sm leading-5",
							subtitleClassName
						)}
					>
						{subtitle}
					</p>
				) : null}
			</div>
			{actions ? (
				<div
					className="flex flex-wrap items-center gap-4"
					data-slot="page-header-actions"
				>
					{actions}
				</div>
			) : null}
		</header>
	);
}
